import React, { useState, useEffect } from "react";
import { useCurrentUser } from "shared/auth/hooks/useCurrentUser";
import { updateUserProfile } from "shared/api/client/orvalHelpers";
import { parseApiError } from "shared/api/client/errorParser";
import { getAuthCookieAttributes } from "shared/auth/utils/cookieUtils";
import DeleteAccountModal from "./modals/DeleteAccountModal";
import ChangePasswordModal from "./modals/ChangePasswordModal";
import ChangeEmailModal from "./modals/ChangeEmailModal";
import { MobileMenu } from "features/shell";

import "styles/legacy/Profile.css";

const MobileProfile: React.FC = () => {
  const user = useCurrentUser();
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);

  const [initialFirstName, setInitialFirstName] = useState("");
  const [initialLastName, setInitialLastName] = useState("");
  const [initialEmail, setInitialEmail] = useState("");

  const [messageBanner, setMessageBanner] = useState("");

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
      setInitialFirstName(user.firstName || "");
      setInitialLastName(user.lastName || "");
      setInitialEmail(user.email || "");
    }
  }, [user]);

  const hasChanges =
    firstName !== initialFirstName ||
    lastName !== initialLastName ||
    email !== initialEmail;

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleDeleteAccount = () => {
    setShowDeleteAccountModal(true);
  };
  const handleEditButton = () => {
    setShowChangeEmailModal(true);
  };
  const handlePasswordReset = () => {
    setShowChangePasswordModal(true);
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (!firstName.trim()) {
      setFirstNameError(true);
      hasError = true;
    } else {
      setFirstNameError(false);
    }

    if (!lastName.trim()) {
      setLastNameError(true);
      hasError = true;
    } else {
      setLastNameError(false);
    }

    if (hasError) return;

    if (!user || !user.id) {
      alert("User not loaded.");
      return;
    }

    try {
      const response = await updateUserProfile(user.id, {
        email,
        firstName,
        lastName,
      });

      setMessageBanner("Profile updated successfully!");
      setTimeout(() => setMessageBanner(""), 3000);
      setFirstName(response?.firstName ?? firstName);
      setLastName(response?.lastName ?? lastName);
      setEmail(response?.email ?? email);
      setInitialFirstName(response?.firstName ?? firstName);
      setInitialLastName(response?.lastName ?? lastName);
      setInitialEmail(response?.email ?? email);
    } catch (error) {
      setMessageBanner(parseApiError(error, "Failed to update profile."));
      console.error(error);
    }
  };

  return (
    <div className="menu-component-mobile">
      <DeleteAccountModal
        open={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        userId={user?.id ?? ""}
      />
      <ChangePasswordModal
        open={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        setSuccessMessage={setMessageBanner}
      />
      <ChangeEmailModal
        open={showChangeEmailModal}
        onClose={() => setShowChangeEmailModal(false)}
        setSuccessMessage={setMessageBanner}
        onEmailChanged={(value) => {
          setEmail(value);
          setInitialEmail(value);
          document.cookie = `userEmail=${encodeURIComponent(value)}; ${getAuthCookieAttributes()}`;
        }}
      />
      <div className="main-container-home-mobile">
        {messageBanner && (
          <div className="message-banner-mobile">
            <div className="error-banner-text-mobile">{messageBanner}</div>
          </div>
        )}
        <div className="menu-component-header-mobile">
          <img
            src="/images/poetics-stream-lockup.svg"
            alt="Poetics Stream"
            className="quima-logo-mobile"
          />
          <img
            src="/images/mobile/hamburger-menu.svg"
            alt="menu"
            className="quima-menu-btn-mobile"
            onClick={toggleMenu}
          />
        </div>
        <div className="menu-component-mid-container-mobile">
          <div className="menu-component-mid-title-container-mobile">
            <div className="menu-component-title-mobile">Profile</div>
            <div className="menu-component-subtitle-mobile">
              Edit your profile details
            </div>
          </div>
          <div className="form-fields-profile-mobile">
            <div
              className="form-group-profile-mobile"
              id={firstNameError ? "incorrect-input-profile" : undefined}
            >
              <label htmlFor="firstName" className="form-label-profile">
                First name<span>*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="form-field-profile-mobile"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div
              className="form-group-profile-mobile"
              id={lastNameError ? "incorrect-input-profile" : undefined}
            >
              <label htmlFor="lastName" className="form-label-profile">
                Last name<span>*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="form-field-profile-mobile"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            {user && user.socialProvider !== "" ? (
              <div className="form-group-profile-mobile">
                <label className="form-label-profile">Linked Account</label>
                <div className="linked-account-container-profile-mobile">
                  <span className="status-dot-mobile" />
                  <img
                    src={`/images/mobile/SSO_buttons/${user.socialProvider.toLowerCase()}.svg`}
                    alt="social svg"
                    className="sso-logo-mobile"
                  />
                  <span className="provider-name-profile">
                    {user.socialProvider}
                  </span>
                  <span className="divider-profile" />
                  <span className="linked-email-profile">{email}</span>
                </div>
              </div>
            ) : (
              <>
                <div className="form-group-profile-mobile">
                  <label className="form-label-profile">Email</label>
                  <div className="email-profile-container">
                    <div className="linked-account-container-profile-mobile">
                      <span className="linked-email-profile">{email}</span>
                    </div>
                    <button
                      className="edit-button-profile"
                      onClick={handleEditButton}
                    >
                      <img
                        src="/images/icons/edit.svg"
                        className="edit-icon-profile"
                        alt="Icon"
                      />
                    </button>
                  </div>
                </div>

                <div
                  className="form-group-profile-mobile"
                  id="form-button-profile"
                >
                  <label className="form-label-profile">Password</label>
                  <button
                    className="password-button-profile"
                    onClick={handlePasswordReset}
                  >
                    Change Password
                  </button>
                </div>
              </>
            )}
            <div className="bottom-buttons-profile-mobile">
              <button
                type="button"
                className="menu-component-pair-btn-mobile"
                onClick={handleSubmit}
                {...(!hasChanges ? { id: "inactive-form-button-profile" } : {})}
                disabled={!hasChanges}
              >
                Save changes
              </button>
              <button
                type="button"
                className="delete-account-button-profile-mobile"
                onClick={handleDeleteAccount}
              >
                Delete account
              </button>
            </div>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      )}
    </div>
  );
};

export default MobileProfile;
